import ScreenWrapper from "@/components/ScreenWrapper"
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import {
  DataTable,
  TABLE_PAGE_SIZE,
  selectColumn,
  createSortableColumn,
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  createCustomColumn,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import { getStudentRegistrations } from "@/api/services/studentRegistrations"
import { studentRegistrationsQueryKeys } from "@/api/query-keys/studentRegistrations"
import { StudentRegistration } from "@/api/types/student"
import { getStudentRegistrationStatusLabel } from "@/utils/getStudentRegistrationStatusLabel"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import { TableHeaderActions } from "@/components/TableHeaderActions"

export const Route = createFileRoute("/beheer/aanmeldingen/")({
  component: RouteComponent,
  validateSearch: createTableSearchSchema(),
})

function RouteComponent() {
  const searchParams = useSearch({ from: "/beheer/aanmeldingen/" })
  const navigate = useNavigate({ from: "/beheer/aanmeldingen/" })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
  } = usePaginatedTableWithUrlState({
    queryKey: studentRegistrationsQueryKeys.all,
    queryFn: getStudentRegistrations,
    pageSize: TABLE_PAGE_SIZE,
    searchParams: {
      ...searchParams,
    },
    navigate,
    storageKey: "/beheer/aanmeldingen",
  })

  const columns: ColumnDef<StudentRegistration>[] = [
    selectColumn(),
    createCustomColumn({
      accessorKey: "id",
      name: "ID",
      id: "id",
      cell: ({ row }: { row: { original: StudentRegistration } }) => (
        <>{row.original.id}</>
      ),
    }),
    createCustomColumn({
      id: "name",
      name: "Naam",
      cell: ({ row }: { row: { original: StudentRegistration } }) =>
        row.original.studentName,
      accessorKey: "studentName",
    }),
    createSortableColumn({ accessorKey: "email", name: "E-mailadres" }),
    createSortableColumn({ accessorKey: "phoneNumberCall", name: "Tel. nr." }),
    createSortableColumn({ accessorKey: "creatorName", name: "Aanmaker" }),
    createSortableColumn({ accessorKey: "projectName", name: "Contract" }),
    createCustomColumn({
      id: "invite_status",
      name: "Status",

      cell: ({ row }: { row: { original: StudentRegistration } }) => {
        return (
          <span>
            {getStudentRegistrationStatusLabel(row.original.inviteStatus)}
          </span>
        )
      },
      accessorKey: "invite_status",
      enableSorting: false,
    }),
    createCustomColumn({
      id: "statusIndicator",
      name: "",
      cell: ({ row }: { row: { original: StudentRegistration } }) => {
        return (
          row.original.inviteStatus === "rejected" && (
            <Link
              onClick={(e) => e.stopPropagation()}
              to="/beheer/aanmeldingen/$studentRegistrationId/opnieuw-indienen"
              params={{
                studentRegistrationId: row.original.id.toString(),
              }}
              className="text-primary-green flex gap-1 items-center w-fit cursor-pointer font-bold"
            >
              Opnieuw indienen
              <ChevronRightIcon className="size-5" />
            </Link>
          )
        )
      },
      accessorKey: "status",
      enableSorting: false,
    }),
  ]

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Aanmeldingen"
        data={data?.data.studentRegistrations || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        columns={columns}
        headerRightActions={
          <>
            <TableHeaderActions
              createAction={{
                label: "Nieuwe cursist",
                to: "/beheer/cursisten/aanmaken",
                entity: "students",
                permissions: "create",
              }}
            />
          </>
        }
      />
    </ScreenWrapper>
  )
}
